import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import type { IContext } from './columns'
import type { GridApi, RowClickedEvent } from 'ag-grid-enterprise'
import type { AgGridReact } from 'ag-grid-react'
import type { ChangeEvent, ReactElement, RefObject } from 'react'
import type { DeviceType, MobileDeviceType } from '~/types/device-type'
import type { IInAppWithAppIconUrl } from '~/types/iap'

import columns from './columns'
import Filters from './Filters'
import PanelContent from './PanelContent'

import Button from '~/components/Buttons/Button'
import ContentLayout from '~/components/ContentLayout'
import Layout from '~/components/Layouts/Layout'
import Panel from '~/components/Panel'
import Section from '~/components/Section'
import ServerSideTableWrapper from '~/components/Tables/ServerSideTableWrapper'
import ViewTitle from '~/components/ViewTitle'
import { CATEGORY_URLS } from '~/const/category-urls'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useTablePagination } from '~/hooks/useTablePagination'
import { useIapContext } from '~/providers/Iap'
import { useAppsManagementListQuery } from '~/queries/application-management'
import { iapKeys, useInAppsListQuery, useRefreshInAppsForAppMutation } from '~/queries/iap'
import { debounce } from '~/utils/function'
import { displayToast } from '~/utils/toast'
import AppSelectorModal from '~/views/DynamicScreens/NavigationGraphs/Details/modals/AppSelectorModal'

import './styles.scss'

const useOnClickOutside = (
  tableRef: RefObject<HTMLDivElement>,
  handleRedirect: VoidFunction,
  gridApi?: GridApi,
  panelRef?: HTMLDivElement
) => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        (panelRef &&
          tableRef.current &&
          (tableRef.current.contains(event.target as Node) || panelRef.contains(event.target as Node))) ||
        !document.getElementById('root')?.contains(event.target as Node)
      ) {
        return
      }
      if (gridApi) {
        handleRedirect()
        gridApi.deselectAll()
      }
    }

    gridApi && gridApi.sizeColumnsToFit()

    panelRef && document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [gridApi, handleRedirect, panelRef, tableRef])
}

const ENTRIES_NAME = 'in-apps-list'

const InAppsView = (): ReactElement => {
  /** Local state */

  const { appKey, deviceType, sku } = useParams<{ appKey?: string; deviceType?: DeviceType; sku?: string }>()

  const { state } = useIapContext()

  const { apps, freeTrial, period, priceRange, type } = state

  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const paginationApi = useTablePagination(ENTRIES_NAME)

  const { page, size } = paginationApi

  const gridRef = useRef<AgGridReact>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  const [search, setSearch] = useState('')

  const [isInSync, setIsInSync] = useState(false)

  const [isAppSelectorModalOpen, setIsAppSelectorModalOpen] = useState(false)

  const [panelRef, setPanelRef] = useState<HTMLDivElement>()

  const [selectedInApp, setSelectedInApp] = useState<IInAppWithAppIconUrl>()

  /** Queries & Mutations */

  const { data: appsList } = useAppsManagementListQuery()

  const { data: inApps } = useInAppsListQuery({
    apps,
    freeTrial,
    limit: size,
    page,
    period,
    priceRange,
    searchQuery: search,
    type,
  })

  const { mutateAsync: refreshInApps } = useRefreshInAppsForAppMutation()

  const rowData = useMemo(() => {
    return inApps?.data?.map((item) => {
      const icon = appsList?.find((app) => app.app_key === item.app_key)?.icon_file_id

      return {
        ...item,
        app_icon_url: icon || '',
      }
    })
  }, [appsList, inApps?.data])

  /** Handlers */

  const handleAppSelectorModalToggle = (isOpen: boolean) => () => setIsAppSelectorModalOpen(isOpen)

  const handleAppToSyncClick = async ({ app_key, device_type }: { app_key: string; device_type: MobileDeviceType }) => {
    if (app_key && device_type) {
      setIsInSync(true)
      try {
        setIsAppSelectorModalOpen(false)
        await refreshInApps({ appKey: app_key, deviceType: device_type })
        await queryClient.invalidateQueries({
          queryKey: iapKeys.inAppsList({
            apps,
            freeTrial,
            limit: size,
            page,
            period,
            priceRange,
            searchQuery: search,
            type,
          }),
        })
        displayToast('In-apps list refreshed', 'success')
      } catch (e) {
        captureException(e)
        displayToast('Could not refresh in-apps', 'error')
      } finally {
        setIsInSync(false)
      }
    }
  }

  const handleSearchChange = useMemo(
    () =>
      debounce((event: ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value)
      }, 500),
    []
  )

  const handleRowClicked = ({ data, node }: RowClickedEvent<IInAppWithAppIconUrl>) => {
    if (!node.isSelected() || !data) {
      return
    }

    navigate(`${CATEGORY_URLS.IN_APP_PURCHASES}/in-apps/${data.app_key}/${data.device_type}/${data.sku}`, {
      replace: true,
    })
  }

  const handleClosePanel = () => {
    if (gridRef.current?.api) gridRef.current.api.deselectAll()
    setPanelRef(undefined)
    navigate(`${CATEGORY_URLS.IN_APP_PURCHASES}/in-apps`, { replace: true })
  }

  const contentRefCallback = (node: HTMLDivElement) => setPanelRef(node)

  const onBadgeClick = (id: string) => navigate(`${CATEGORY_URLS.IN_APP_PURCHASES}/offers/editor/${id}`)

  /** Effects */

  useDocumentTitle('IAP - In-apps')

  useEffect(() => {
    if (appKey && deviceType && sku && rowData) {
      const iap = rowData.find((data) => appKey === data.app_key && deviceType === data.device_type && sku === data.sku)

      if (iap) {
        setSelectedInApp(iap)

        if (gridRef.current?.api) {
          gridRef.current.api.forEachNode((node) => {
            if (node.data.app_key === appKey && node.data.device_type === deviceType && node.data.sku === sku) {
              node.setSelected(true)
            }
          })
        }
      }
    }
  }, [appKey, deviceType, rowData, selectedInApp, sku])

  useOnClickOutside(tableRef, handleClosePanel, gridRef.current?.api, panelRef)

  /** Render */

  const context: IContext = { onBadgeClick }

  return (
    <>
      <Layout className="in-apps-view">
        <ViewTitle className="layout-view-title in-apps-view__title-container">
          <span>IN-APPS LISTING</span>
          <Button
            className="in-apps-view__title-container__sync-button"
            disabled={isInSync}
            onClick={handleAppSelectorModalToggle(true)}
          >
            {isInSync ? <div className="lds-dual-ring" /> : 'Synchronise In-Apps with store'}
          </Button>
        </ViewTitle>
        <ContentLayout className="in-apps-view__content">
          <Section className="in-apps-view__content__section">
            <Filters />
            <div className="in-apps-view__content__section__table-wrapper">
              <ServerSideTableWrapper
                columnDefs={columns}
                context={context}
                currentPage={page}
                entriesName={ENTRIES_NAME}
                onRowClicked={handleRowClicked}
                onSearchChange={handleSearchChange}
                paginationApi={paginationApi}
                ref={gridRef}
                rowData={rowData}
                rowHeight={69}
                rowSelection={{
                  checkboxes: false,
                  enableClickSelection: true,
                  mode: 'singleRow',
                }}
                tableRef={tableRef}
                total={inApps?.total || 0}
                withSearchBar
              />
            </div>
          </Section>
        </ContentLayout>
      </Layout>
      <AppSelectorModal
        isOpen={isAppSelectorModalOpen}
        onAppSelectorRowClicked={handleAppToSyncClick}
        onClose={handleAppSelectorModalToggle(false)}
        selectedApp={{ id: '', name: '' }}
        title="Select an app to sync in-apps"
      />
      <Panel
        className="in-app-details-panel"
        isOpen={!!appKey && !!deviceType && !!sku}
        onClosePanel={handleClosePanel}
        panelRef={contentRefCallback}
      >
        <PanelContent inAppDetails={selectedInApp} />
      </Panel>
    </>
  )
}

export default InAppsView
